import { sparkleUtils } from '@/shared/sparkle-utils';
import { ImportedDataType } from './ImportedData.types';
import { getCacheKeySegment, getItemFolderName } from './ImportedDataRequest.service';
import { SitePathMappingService } from './SitePathMapping.service';

const IMPORTED_DATA_ROOT_PATH = '/sitecore/content/Health System Enterprise/Global/Imported Data';

abstract class ImportedDataRequestBase implements IImportedDataRequest {
  abstract query: string;
  cacheKey: string;
  isInvalid: boolean;
  variables: {
    [key: string]: unknown;
  };

  constructor(public slug: string, public siteName: string, dataType: ImportedDataType) {
    const pathMapping = SitePathMappingService.MapSitePath(siteName);
    const itemFolderName = getItemFolderName(dataType);
    const cacheKeySegment = getCacheKeySegment(dataType);

    this.isInvalid = sparkleUtils.isEmptyOrWhiteSpace(slug) || pathMapping == null;

    this.siteName = pathMapping?.website ?? '';

    this.cacheKey = `${slug}:${cacheKeySegment}`;

    this.variables = {
      itemPath: `${IMPORTED_DATA_ROOT_PATH}/${itemFolderName}/${pathMapping?.region}/${pathMapping?.website}/${this.slug}`,
      slug: this.slug,
    };
  }
}

export class SparkleProviderImportedDataRequest extends ImportedDataRequestBase {
  query = `query ImportedSparkleProviderItem($itemPath: String!) {
          item(path: $itemPath, language: "en") {
            browserTitle: field(name: "BrowserTitle") { value }
            canonicalUrl: field(name: "CanonicalUrl") { value }
            pageKeywords: field(name: "MetaKeywords") { value }
            pageDescription: field(name: "MetaDescription") { value }
            pageType: field(name: "OpenGraphType") { value }
            imageUrl: field(name: "ImageUrl") { value }
            imageWidth: field(name: "ImageWidth") { value }
            imageHeight: field(name: "ImageHeight") { value }
            imageType: field(name: "ImageType") { value }
            firstName: field(name: "FirstName") { value }
            lastName: field(name: "LastName") { value }
            gender: field(name: "Gender") { value }
            jsonPayload: field(name: "JSONPayload") { value }
            navigationTitle: field(name: "NavigationTitle") { value }
            siteName: field(name: "OpenGraphSiteName") { value }
          }
        }`;

  constructor(slug: string, siteName: string) {
    super(slug, siteName, ImportedDataType.SparkleProvider);
  }
}

export class SparkleLocationImportedDataRequest extends ImportedDataRequestBase {
  query = `query ImportedSparkleLocationItem($itemPath: String!) {
          item(path: $itemPath, language: "en") {
            browserTitle: field(name: "BrowserTitle") { value }
            canonicalUrl: field(name: "CanonicalUrl") { value }
            pageKeywords: field(name: "MetaKeywords") { value }
            pageDescription: field(name: "MetaDescription") { value }
            pageType: field(name: "OpenGraphType") { value }
            imageUrl: field(name: "ImageUrl") { value }
            imageWidth: field(name: "ImageWidth") { value }
            imageHeight: field(name: "ImageHeight") { value }
            imageType: field(name: "ImageType") { value }
            publishedTime: field(name: "PublishedTime") { value }
            modifiedTime: field(name: "ModifiedTime") { value }
            section: field(name: "Section") { value }
            jsonPayload: field(name: "JSONPayload") { value }
            navigationTitle: field(name: "NavigationTitle") { value }
            siteName: field(name: "OpenGraphSiteName") { value }
          }
        }`;

  constructor(slug: string, siteName: string) {
    super(slug, siteName, ImportedDataType.SparkleLocation);
  }
}

export class EventDetailImportedDataRequest extends ImportedDataRequestBase {
  query = `query ImportedSparkleEventItem($itemPath: String!) {
          item(path: $itemPath, language: "en") {
            browserTitle: field(name: "BrowserTitle") { value }
            canonicalUrl: field(name: "CanonicalUrl") { value }
            pageKeywords: field(name: "MetaKeywords") { value }
            pageDescription: field(name: "MetaDescription") { value }
            pageType: field(name: "OpenGraphType") { value }
            publishedTime: field(name: "PublishedTime") { value }
            modifiedTime: field(name: "ModifiedTime") { value }
            section: field(name: "Section") { value }
            jsonPayload: field(name: "JSONPayload") { value }
            navigationTitle: field(name: "NavigationTitle") { value }
            siteName: field(name: "OpenGraphSiteName") { value }
          }
        }`;

  constructor(slug: string, siteName: string) {
    super(slug, siteName, ImportedDataType.SparkleEvent);
  }
}

export type GraphQLResults = {
  item: {
    browserTitle?: { value: string };
    canonicalUrl?: { value: string };
    pageKeywords?: { value: string };
    pageDescription?: { value: string };
    pageType?: { value: string };
    imageUrl?: { value: string };
    imageWidth?: { value: string };
    imageHeight?: { value: string };
    imageType?: { value: string };
    firstName?: { value: string };
    lastName?: { value: string };
    gender?: { value: string };
    jsonPayload?: { value: string };
    navigationTitle?: { value: string };
    siteName?: { value: string };
    publishedTime?: { value: string };
    modifiedTime?: { value: string };
    section?: { value: string };
  };
};

export interface IImportedDataRequest {
  isInvalid: boolean;
  query: string;
  variables: {
    [key: string]: unknown;
  };
  cacheKey: string;
  slug: string;
  siteName: string;
}

export function CreateDataRequest(
  importedDataType: ImportedDataType,
  slug: string,
  siteName: string
): IImportedDataRequest {
  switch (importedDataType) {
    case ImportedDataType.SparkleEvent:
      return new EventDetailImportedDataRequest(slug, siteName);
    case ImportedDataType.SparkleLocation:
      return new SparkleLocationImportedDataRequest(slug, siteName);
    case ImportedDataType.SparkleProvider:
      return new SparkleProviderImportedDataRequest(slug, siteName);
  }
}
